/* Idea Sandbox — ported from the prototype's Screens.sandbox(). Standalone tool:
   paste a paragraph, get an instant read vs the thesis (idea-strength score +
   traffic-lit reads) from POST /deal/sandbox/analyse, then invite the founder to
   apply or save it. The analyse call persists a DealSandboxIdea for the firm. */
import React, { useEffect, useState } from 'react';

import { useToast } from '../components/Toast';
import { levelColor } from '../helpers';
import { analyseSandbox, ok } from '../api';

const RING_C = 2 * Math.PI * 40;
const Dot = ({ level }) => <span className="finding__dot" style={{ background: levelColor(level) }} />;

/* Starter prompt so the textarea isn't empty on first load (placeholder copy only). */
const SAMPLE_IDEA = 'We’re building a marketplace that lets independent pharmacies pool their purchasing to get hospital-grade pricing from wholesalers. 4,000 UK pharmacies are overpaying by 12–18% today. We take 1% of transaction volume.';

function ScoreRing({ score }) {
	const [offset, setOffset] = useState(RING_C);
	useEffect(() => {
		const t = setTimeout(() => setOffset(RING_C * (1 - (score || 0) / 100)), 80);
		return () => clearTimeout(t);
	}, [score]);
	return (
		<div className="ring" style={{ width: 96, height: 96 }}>
			<svg width="96" height="96" viewBox="0 0 96 96">
				<circle className="ring__track" cx="48" cy="48" r="40" />
				<circle className="ring__val" cx="48" cy="48" r="40" strokeDasharray={RING_C} strokeDashoffset={offset} />
			</svg>
			<div className="ring__num" style={{ fontSize: 26 }}>{score}<small>/ 100</small></div>
		</div>
	);
}

export default function Sandbox() {
	const showToast = useToast();
	const [idea, setIdea] = useState(SAMPLE_IDEA);
	const [result, setResult] = useState(null);     // { score, reads }
	const [running, setRunning] = useState(false);

	const run = async () => {
		if (running || !idea.trim()) return;
		setRunning(true);
		showToast('Intelligentsia is reading the idea…');
		const res = await analyseSandbox(idea.trim());
		setRunning(false);
		if (!ok(res)) return;
		setResult({ score: res.body.score, reads: res.body.reads || [] });
	};
	const invite = () => {
		showToast('Funding-form invite sent — a full application starts the real pipeline');
		setTimeout(() => showToast('Founder account will be auto-created on Diligentsia', true), 900);
	};

	return (
		<main className="page screen" style={{ maxWidth: 1020 }}>
			<div className="kicker">Idea sandbox · standalone tool</div>
			<div className="page__title" style={{ marginBottom: 2 }}>No deck yet? Test the idea.</div>
			<div className="page__sub" style={{ marginBottom: 18 }}>Paste a paragraph — office hours, accelerator applications, a founder's cold email — and get an instant read against your thesis.</div>

			<div className="card" style={{ marginBottom: 12 }}>
				<div style={{ fontSize: 13, color: 'var(--dlg-faint)', marginBottom: 8 }}>The idea, in the founder's words</div>
				<textarea className="rationale-box" style={{ minHeight: 96, fontSize: 14 }} value={idea} onChange={e => setIdea(e.target.value)} />
				<button className="pill pill--primary" style={{ marginTop: 12 }} onClick={run} disabled={running}>{running ? 'Analysing…' : result ? 'Re-analyse idea' : 'Analyse idea'}</button>
			</div>

			{result && (
				<>
					<div className="grid" style={{ gridTemplateColumns: '220px 1fr' }}>
						<div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 22 }}>
							<ScoreRing score={result.score} />
							<div style={{ fontSize: 12.5, color: 'var(--dlg-muted)', textAlign: 'center', marginTop: 10 }}>Idea strength vs your thesis</div>
						</div>
						<div className="card">
							<span className="chip chip--blue" style={{ marginBottom: 8 }}>✦ First read by Intelligentsia</span>
							{result.reads.map((r, i) => <div key={i} className="finding"><Dot level={r.level} /><span>{r.text}</span></div>)}
						</div>
					</div>
					<div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
						<button className="pill pill--primary" onClick={invite}>Invite founder to apply →</button>
						<button className="pill pill--outline" onClick={() => showToast('Saved to sandbox — revisit any time')}>Save to sandbox</button>
					</div>
				</>
			)}
		</main>
	);
}
